/**
 * PRV-008: Tabla de Solicitudes de Edición de Factibilidad
 *
 * Muestra las solicitudes de Edición pendientes de aprobaci&oacute;n del supervisor.
 * Permite aprobar o rechazar las solicitudes.
 */

import { PermissionsEnum } from "@/auth/interfaces";
import {
  hasPermission,
  TABLE_CONSTANTS,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";
import { useFetchGetSolicitudesEdicion } from "@/shared/actions/erp/solicitud_edicion_factibilidad";
import GeneralTable from "@/shared/components/ui/GeneralTable";
import {
  ISolicitudEdicionFactibilidad,
  EstadoSolicitudEdicionEnum
} from "@/shared/interfaces/erp/factibilidad_corporativo";
import { CheckCircle, Cancel, Visibility } from "@mui/icons-material";
import { Chip, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { useMemo, useState } from "react";
import { ModalGestionSolicitudEdicion } from "./ModalGestionSolicitudEdicion";

export default function TableSolicitudesEdicion() {
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState<ISolicitudEdicionFactibilidad | null>(null);
  const [openModalGestion, setOpenModalGestion] = useState(false);
  const [accionInicial, setAccionInicial] = useState<'ver' | 'aprobar' | 'rechazar'>('ver');

  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  const {
    data: solicitudesPagingRes,
    isLoading,
    isRefetching,
  } = useFetchGetSolicitudesEdicion({
    page: pageIndex + 1,
    page_size: pageSize,
    ...filterObject,
    estado_solicitud: EstadoSolicitudEdicionEnum.EN_ESPERA,
  });

  const columns = useMemo<MRT_ColumnDef<ISolicitudEdicionFactibilidad>[]>(
    () => [
      {
        accessorKey: "codigo",
        header: "C&oacute;digo Solicitud",
        size: 150,
        enableColumnFilter: true,
      },
      {
        accessorKey: "factibilidad_corporativa_data.codigo",
        header: "Factibilidad",
        size: 150,
        Cell: ({ row }) => {
          const factibilidad = row.original.factibilidad_corporativa_data;
          return factibilidad?.codigo || 'N/A';
        },
      },
      {
        accessorKey: "factibilidad_corporativa_data.prospecto_corporativo_data.nombre_comercial",
        header: "Cliente",
        size: 200,
        Cell: ({ row }) => {
          const factibilidad = row.original.factibilidad_corporativa_data;
          const prospecto = factibilidad?.prospecto_corporativo_data;
          return prospecto?.nombre_comercial || prospecto?.razon_social || 'N/A';
        },
      },
      {
        accessorKey: "factibilidad_corporativa_data.prospecto_corporativo_data.identificacion",
        header: "Identificaci&oacute;n",
        size: 130,
        Cell: ({ row }) => {
          const factibilidad = row.original.factibilidad_corporativa_data;
          return factibilidad?.prospecto_corporativo_data?.identificacion || 'N/A';
        },
      },
      {
        accessorKey: "motivo",
        header: "Motivo",
        size: 250,
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue<string>() || ''} placement="top">
            <Typography
              variant="body2"
              sx={{
                maxWidth: '250px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {cell.getValue<string>()}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: "user_create_data",
        header: "Solicitante",
        size: 180,
        Cell: ({ cell }) => {
          const user = cell.getValue<any>();
          return user ? `${user.first_name} ${user.last_name}` : 'N/A';
        },
      },
      {
        accessorKey: "estado_solicitud",
        header: "Estado",
        size: 120,
        Cell: ({ cell }) => {
          const estado = cell.getValue<string>();
          const color =
            estado === 'EN ESPERA' ? 'warning' :
              estado === 'APROBADO' ? 'success' :
                estado === 'RECHAZADO' ? 'error' : 'default';
          return <Chip label={estado} color={color} size="small" />;
        },
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha Solicitud",
        size: 160,
        Cell: ({ cell }) => {
          const fecha = cell.getValue<string>();
          return fecha ? new Date(fecha).toLocaleString('es-EC') : 'N/A';
        },
      },
    ],
    []
  );

  const handleOpenModal = (
    solicitud: ISolicitudEdicionFactibilidad,
    accion: 'ver' | 'aprobar' | 'rechazar'
  ) => {
    setSolicitudSeleccionada(solicitud);
    setAccionInicial(accion);
    setOpenModalGestion(true);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <GeneralTable<ISolicitudEdicionFactibilidad>
            columns={columns}
            data={solicitudesPagingRes?.data?.data || []}
            isLoading={isLoading}
            isRefetching={isRefetching}
            enableGlobalFilter={false}
            enableManualFiltering={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            enableSorting={false}
            pagination={pagination}
            onPaging={setPagination}
            rowCount={solicitudesPagingRes?.data?.count}

            // Acciones
            enableEditing={hasPermission(PermissionsEnum.api_change_factibilidadcoorporativa)}
            actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH_MEDIUM + 30}
            positionActionsColumn="first"

            // Custom actions
            showCustomActionBtnSpace={true}
            customActionBtnSpace={(row) => (
              <Grid container spacing={0.5} wrap="nowrap">
                {/* Bot&oacute;n Ver Detalles */}
                <Grid item>
                  <Tooltip title="Ver Detalles">
                    <IconButton
                      color="info"
                      size="small"
                      onClick={() => handleOpenModal(row, 'ver')}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>

                {/* Bot&oacute;n Aprobar */}
                <Grid item>
                  <Tooltip title="Aprobar Solicitud">
                    <IconButton
                      color="success"
                      size="small"
                      onClick={() => handleOpenModal(row, 'aprobar')}
                    >
                      <CheckCircle fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>

                {/* Bot&oacute;n Rechazar */}
                <Grid item>
                  <Tooltip title="Rechazar Solicitud">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => handleOpenModal(row, 'rechazar')}
                    >
                      <Cancel fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            )}

            canDelete={false}
            canEdit={false}
          />
        </Grid>
      </Grid>

      {/* Modal de Gesti&oacute;n */}
      <ModalGestionSolicitudEdicion
        open={openModalGestion}
        setOpen={setOpenModalGestion}
        solicitud={solicitudSeleccionada}
        accionInicial={accionInicial}
      />
    </>
  );
}
